import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@/components/ui';
import messageBoardService, {
  type MessageBoardPostReads,
} from '../../../services/messageBoardService';
import { formatMessageBoardTime } from '../utils/formatMessageBoardTime';

export interface MessageBoardReadReceiptsProps {
  postId: number;
}

type ReceiptFilter = 'all' | 'seen' | 'notSeen';

interface ReceiptRow {
  key: string;
  name: string;
  status: 'seen' | 'notSeen';
  seenAt?: string;
}

const PAGE_SIZE = 10;

const FILTERS: Array<{ id: ReceiptFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'seen', label: 'Seen' },
  { id: 'notSeen', label: 'Not seen' },
];

function rowsFor(reads: MessageBoardPostReads, filter: ReceiptFilter): ReceiptRow[] {
  const seen: ReceiptRow[] = reads.seen.map((person) => ({
    key: `seen-${person.name}-${person.seenAt}`,
    name: person.name,
    status: 'seen',
    seenAt: person.seenAt,
  }));
  const notSeen: ReceiptRow[] = reads.notSeen.map((person) => ({
    key: `not-seen-${person.name}`,
    name: person.name,
    status: 'notSeen',
  }));

  if (filter === 'seen') return seen;
  if (filter === 'notSeen') return notSeen;
  return [...notSeen, ...seen];
}

const MessageBoardReadReceipts: React.FC<MessageBoardReadReceiptsProps> = ({ postId }) => {
  const [reads, setReads] = useState<MessageBoardPostReads | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReceiptFilter>('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFilter('all');
    setPage(1);

    messageBoardService
      .getReads(postId)
      .then((next) => {
        if (!cancelled) setReads(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReads(null);
        setError(err instanceof Error ? err.message : 'Failed to load read receipts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [postId]);

  const rows = useMemo(() => (reads ? rowsFor(reads, filter) : []), [reads, filter]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visible = rows.slice(pageStart, pageStart + PAGE_SIZE);

  if (loading && !reads) {
    return (
      <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-content-secondary">
        <Spinner size="sm" />
        <span>Loading who has seen this…</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-danger" role="alert">
        {error}
      </p>
    );
  }

  if (!reads) return null;

  const counts: Record<ReceiptFilter, number> = {
    all: reads.seen.length + reads.notSeen.length,
    seen: reads.seen.length,
    notSeen: reads.notSeen.length,
  };

  const selectFilter = (next: ReceiptFilter) => {
    setFilter(next);
    setPage(1);
  };

  return (
    <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-3">
      <div role="tablist" aria-label="Read receipt status" className="tw-flex tw-flex-wrap tw-gap-2">
        {FILTERS.map((tab) => {
          const selected = filter === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => selectFilter(tab.id)}
              className={`tw-inline-flex tw-min-h-9 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-3 tw-py-1 tw-text-sm ${
                selected
                  ? 'tw-border-primary tw-bg-primary/10 tw-font-medium tw-text-primary'
                  : 'tw-border-line-input tw-bg-surface tw-text-content hover:tw-bg-surface-hover'
              }`}
            >
              {tab.label}
              <span className="tw-tabular-nums">{counts[tab.id]}</span>
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <p className="tw-mb-0 tw-text-xs tw-text-content-secondary">
          {filter === 'seen'
            ? 'No one has opened this yet.'
            : 'Everyone this was sent to has seen it.'}
        </p>
      ) : (
        <ul className="tw-mb-0 tw-list-none tw-divide-y tw-divide-line tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-0">
          {visible.map((person) => (
            <li key={person.key} className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5">
              <span
                className={`tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full ${
                  person.status === 'seen' ? 'tw-bg-success' : 'tw-bg-primary'
                }`}
                aria-hidden
              />
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-text-content">
                {person.name}
              </span>
              {person.status === 'seen' && person.seenAt ? (
                <span className="tw-shrink-0 tw-text-xs tw-font-medium tw-text-success">
                  Seen{' '}
                  <time dateTime={person.seenAt}>{formatMessageBoardTime(person.seenAt)}</time>
                </span>
              ) : (
                <span className="tw-shrink-0 tw-text-xs tw-text-content-muted">Not seen</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {rows.length > PAGE_SIZE ? (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-xs tw-text-content-muted">
          <span className="tw-tabular-nums">
            {pageStart + 1}–{pageStart + visible.length} of {rows.length}
          </span>
          <div className="tw-flex tw-gap-2">
            <button
              type="button"
              className="tw-min-h-9 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-text-content disabled:tw-opacity-50"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="tw-min-h-9 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-text-content disabled:tw-opacity-50"
              disabled={safePage >= totalPages}
              onClick={() => setPage(safePage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MessageBoardReadReceipts;
